// =============================================================================
// client/src/components/CompleteTaskModal.jsx | v1.0 | SQ-S1 | "Task Completed"
// =============================================================================
// Chrome copied from ConfirmModal.jsx (fixed inset-0 z-50, role=dialog) —
// copied, not extended: ConfirmModal is deliberately dumb and owns no form
// state, and this one owns two fields. Used by the board page and the profile
// tab; both hand the result to POST /api/call-sheet/tasks/:id/complete.
//
//   <CompleteTaskModal
//     open task today busy
//     onConfirm={({ completed_on, notes }) => …}
//     onCancel={…}
//   />
//
// completed_on defaults to `today` (the server's calendar date) and is
// backdatable via DateTriad; the server refuses a future date and that refusal
// renders verbatim in the caller's toast.
// =============================================================================

import { useEffect, useState } from 'react';
import DateTriad from './DateTriad.jsx';
import { recruitName, seqLabel, taskTitle } from '../lib/callSheet.js';

export default function CompleteTaskModal({ open, task, today, busy = false, onConfirm, onCancel }) {
  const [completedOn, setCompletedOn] = useState(today || '');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) { setCompletedOn(today || ''); setNotes(''); }
  }, [open, today, task?.id]);

  if (!open || !task) return null;

  const armed = !!completedOn && !busy;
  const who = recruitName(task);
  const seq = seqLabel(task);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Mark task completed"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="font-display font-black text-hunter text-xl mb-1">Task Completed</h2>
        <p className="text-sm text-silver-mute mb-5">
          <span className="font-bold text-silver-text">{taskTitle(task)}</span>
          {seq ? <span className="text-silver-mute"> · {seq}</span> : null}
          {who !== '—' ? <span className="text-silver-mute"> · {who}</span> : null}
        </p>

        <div className="mb-4">
          <label className="label-dark mb-1 block">Completed on</label>
          <DateTriad value={completedOn} onChange={setCompletedOn} disabled={busy} ariaLabel="Completed on" compact={false} />
          <p className="text-xs text-silver-mute mt-1">Defaults to today. Backdate if the work was done earlier.</p>
        </div>

        <div className="mb-6">
          <label className="label-dark mb-1 block">Notes <span className="font-normal text-silver-mute">(optional)</span></label>
          <textarea
            className="input min-h-[5rem]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            placeholder="What was delivered, anything the next person should know"
            maxLength={2000}
          />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ completed_on: completedOn, notes: notes.trim() || null })}
            disabled={!armed}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? 'Saving…' : 'Mark Completed'}
          </button>
        </div>
      </div>
    </div>
  );
}
